import { createSubstore } from 'storeon-substore';
import type { Store } from 'store/types';
import { requestServiceList, requestServicePriceList } from './services-actions';
import { FETCH_SERVICE_LIST, FETCH_SERVICE_PRICE_LIST, SERVICES_STORE_NAME } from './services-constants';
import { ServicesStore } from './services-types';

const INITIAL_STATE: ServicesStore = {
  serviceList: null,
  error: null,
  loading: false,
  servicePrice: null,
  servicePriceLoading: false,
  servicePriceError: null,
} as const;

export const services = (parentStore: Store) => {
  const store = createSubstore(parentStore, SERVICES_STORE_NAME);
  store.on('@init', () => INITIAL_STATE);
  store.on(FETCH_SERVICE_LIST.init, async (_, serviceId: string) => {
    const { loading } = store.get();
    const { shops, modelJson, orchestratorHost, useCredentials, apiKey, requestID } = parentStore.get();

    if (!modelJson.model || !shops.shopList || !orchestratorHost || loading) {
      return Promise.resolve();
    }

    store.dispatch(FETCH_SERVICE_LIST.request);

    try {
      const serviceList = await requestServiceList(shops.shopList, serviceId, {
        orchestratorHost,
        useCredentials,
        apiKey,
        requestID,
      });
      store.dispatch(FETCH_SERVICE_LIST.success, serviceList);
    } catch (e) {
      store.dispatch(FETCH_SERVICE_PRICE_LIST.failure, JSON.stringify(e));
    }

    return Promise.resolve();
  });
  store.on(FETCH_SERVICE_LIST.request, state => ({ ...state, loading: true }));
  store.on(FETCH_SERVICE_LIST.failure, (_, error) => ({ error, serviceList: null, loading: false }));
  store.on(FETCH_SERVICE_LIST.success, (_, res) => ({ serviceList: res, error: null, loading: false }));

  store.on(FETCH_SERVICE_PRICE_LIST.init, async (_, serviceIdList) => {
    const { modelJson, orchestratorHost, useCredentials, apiKey, requestID, shops } = parentStore.get();

    if (!modelJson.model || !shops.shopList || !orchestratorHost) {
      return Promise.resolve();
    }

    store.dispatch(FETCH_SERVICE_PRICE_LIST.request);
    try {
      const servicePriceList = await requestServicePriceList(shops.shopList, serviceIdList, {
        orchestratorHost,
        useCredentials,
        apiKey,
        requestID,
      });

      const result = servicePriceList.reduce((acc, [servicePrice]) => {
        if (servicePrice.isSaleable && servicePrice.price > 0) {
          return {
            ...acc,
            [servicePrice.serviceId]: servicePrice.price,
          };
        }
        return acc;
      }, {});

      store.dispatch(FETCH_SERVICE_PRICE_LIST.success, result);
    } catch (e) {
      store.dispatch(FETCH_SERVICE_PRICE_LIST.failure, JSON.stringify(e));
    }

    return Promise.resolve();
  });
  store.on(FETCH_SERVICE_PRICE_LIST.request, state => ({
    ...state,
    servicePriceLoading: true,
    servicePriceError: null,
  }));
  store.on(FETCH_SERVICE_PRICE_LIST.failure, (state, servicePriceError) => ({
    ...state,
    servicePriceLoading: false,
    servicePriceError,
  }));
  store.on(FETCH_SERVICE_PRICE_LIST.success, (state, servicePrice) => ({
    ...state,
    servicePrice,
    servicePriceLoading: false,
  }));
};
